<template>
  <v-main role="presentation" class="white_background">
    <v-container text-xs-center>
      <div v-if="isProcessing" class="centerProgress">
        <v-progress-circular indeterminate :size="size" :width="width" :color="color" />
      </div>
      <v-row class="widthForContent">
        <v-col text-xs-center my-4>
          <v-row>
            <v-col cols="12" class="text-center font-size--20 font-weight-bold">
              <div>
                {{ $t('L_bio_success') }}
              </div>
              <div v-if="isFaceId">
                {{ $t('L_you_have_enabled_face_id') }}
              </div>
              <div v-else-if="isTouchId">
                {{ $t('L_you_have_enabled_touch_id') }}
              </div>
            </v-col>

            <v-col cols="12" class="text-center" my-4>
              <v-img
                height="257"
                width="236"
                src="@/assets/SuccessBiometrics.svg"
                class="mx-auto"
                alt="Mobile phone successful enabled biometrics"
              />
            </v-col>
          </v-row>
          <div
            class="mx-auto mb-12 mt-4"
            :class="$vuetify.display.xs ? 'overall-div-card-smaller' : 'overall-div-card-larger'"
          >
            <v-row>
              <v-col
                cols="12"
                class="text-center regular_body_text"
                font-weight-regular
                font-size--20
              >
                <span v-if="isFaceId">{{ $t('L_you_can_now_login_with_faceid') }}</span>
                <span v-else-if="isTouchId">{{ $t('L_you_can_now_login_with_touchid') }}</span>
              </v-col>
            </v-row>
          </div>
          <v-row text-xs-center class="outerlayout">
            <div class="innerLayout">
              <v-col cols="12" my-4>
                <OnexButton
                  block
                  class="btn_default bg-primary mx-4 px-4 font-size--20"
                  @click="goToNextFlow"
                >
                  {{ $t('L_ok') }}
                </OnexButton>
              </v-col>
            </div>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { LOCALES } from '@/shared/models/constants.models';
import { hasFaceId, hasTouchId } from '@/plugins/auth/utils/sessionManager';
import { useGlobalStore } from '@/stores/global/global.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { RouteNames } from '@/router/routeNames';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { mapStores } from 'pinia';
import { OnexButton } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'SuccessBiometrics',
  components: {
    OnexButton,
  },
  props: {
    color: {
      type: String,
      default: '#063562',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    size: {
      type: Number,
      default: 70,
    },
    width: {
      type: Number,
      default: 7,
    },
  },
  data: () => ({
    deviceType: null,
    languagesPrefs: window.appConfig.languagePreferences,
    languages: [] as string[],
    isProcessing: true,
    isFaceId: false,
    isTouchId: false,
    canUseFaceId: false,
    canUseTouchId: false,
    biometricsStrengthStrong: false,
    showErrorDuringBiometricsMessage: false,
  }),
  computed: { ...mapStores(useGlobalStore, useCustomerStore) },
  methods: {
    async goToNextFlow() {
      const deeplinkroute = window.localStorage.getItem('deeplinkroute'); // ? this is a path, NOT name of a route
      let nextRoute;
      if (this.globalStore.hasTermsOfUseTask) nextRoute = RouteNames.TERMS_OF_USE;
      else if (this.globalStore.hasPolicyAckTask) nextRoute = RouteNames.POLICY_ACKNOWLEDGEMENT;
      else {
        window.localStorage.removeItem('deeplinkroute'); // remove the deeplink route
        nextRoute = deeplinkroute ?? RouteNames.DASHBOARD; // there are no post login tasks
      }
      await this.$router.push(deeplinkroute ? { path: nextRoute } : { name: nextRoute });
    },
    updateLocale(language: LOCALES) {
      this.globalStore.updateLocale(language);
    },
  },
  async mounted() {
    await Promise.all([hasFaceId(), hasTouchId()]).then(([_hasFaceId, _hasTouchId]) => {
      // Currently there are android devices that have both come back as true
      // however because the biometric strength is not strong for faceId only the touchId is used
      // Capacitor is not returning which one it is using so using the following logic.
      if (CapacitorFacade.isAndroid()) {
        if (_hasTouchId) {
          this.isFaceId = false;
        } else {
          this.isFaceId = _hasFaceId;
        }
      }
      this.isTouchId = _hasTouchId;
      if (CapacitorFacade.isIos()) {
        if (_hasFaceId) {
          this.isFaceId = _hasFaceId;
        } else {
          this.isTouchId = true;
        }
      }
    });
    this.isProcessing = false;
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.background {
  min-height: 100%;
  background-image: linear-gradient(to top, #607c97, #063562);
}

.white_background {
  min-height: 100%;
  background-color: white;
}

.css_locale {
  background-color: white;
}

.css_app_update {
  font-size: 20px;
  font-weight: bold;
}

.regular_body_text {
  font-size: 20px;
}

.container {
  max-width: 960px !important;
}

.widthForContent {
  max-width: 600px !important;
  margin: auto;
}

.btn_default {
  min-width: 250px;
  max-width: 350px;
  margin: auto;
  height: 48px;
  font-size: 18px;
  font-weight: 600;
  line-height: 27px;
}

.outerlayout {
  padding-bottom: 50px;
}

.innerLayout {
  margin: auto !important;
  padding-bottom: 300px;
}

.btn_white {
  background-color: white !important;
}

.centerProgress {
  left: calc(50% + -38px);
  top: 50vh;
  position: absolute;
}
</style>
